import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../data/content";

export const Nav = ({ scrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-brand-base/80 backdrop-blur-xl border-b border-brand-line"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo onClick={() => go("hero")} showTagline />

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-${l.id}`}
                className="relative font-mono text-[11px] uppercase tracking-[0.2em] text-brand-soft hover:text-brand-text transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand group-hover:w-full transition-all duration-300" />
              </button>
            ))}
            <button
              onClick={() => go("contact")}
              data-testid="nav-cta"
              className="ml-2 px-5 py-2.5 border border-brand text-brand hover:bg-brand hover:text-brand-base transition-colors duration-300 font-mono text-[11px] uppercase tracking-[0.2em]"
            >
              Enquire
            </button>
          </nav>

          <button
            className="md:hidden text-brand-text"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-brand-base md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-brand-line">
              <Logo onClick={() => go("hero")} />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu" className="text-brand-text">
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="font-display text-5xl text-brand-text hover:text-brand transition-colors text-left py-2"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
